import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useParams } from 'react-router-dom';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { Event } from '@domain/event';
import { createPathToDuplicate, createPreviewUrl } from '@utils/create-paths';
import { isUuid } from '@utils/is-uuid';
import moment from 'moment';

import { useGetEvent } from './use-get-event';

export interface CurrentEvent extends Event {
  isLiveEvent: boolean;
  isPublished: boolean;
  venue: string;
  timezone: string;
  path: string;
  duplicatePath?: string;
}

export const useCurrentEvent = () => {
  const params = useParams<{ eventId: string; companyId: string }>();
  const location = useLocation();

  const match = matchPath({ path: '/:companyId/:clubSlug/events/:eventId/*' }, location.pathname);

  const rawEventId = params.eventId ?? match?.params.eventId;
  const eventId = rawEventId && isUuid(rawEventId) ? rawEventId : undefined;
  const companyId = params.companyId ?? match?.params.companyId;

  const { i18n } = useTranslation();
  const { isWebView } = useContext(FeatureFlagContext);
  const {
    companyState: { defaultChannel },
  } = useContext(CompanyClubContext);

  const { data: event, ...rest } = useGetEvent({
    eventId,
    options: { enabled: !!eventId },
  });

  const derivedState = useMemo(() => {
    if (!event) return null;

    const channelId = defaultChannel?.slug;

    return {
      ...event,
      isLiveEvent: event.endingTime > moment().unix(),
      isPublished: event.settings.isPublished,
      venue: event.club.name,
      timezone: event.club.city.timezone,
      path: createPreviewUrl({
        event,
        locale: i18n.language,
        defaultChannel: channelId,
        isWebView,
      }),
      duplicatePath: event.settings.isPublished
        ? createPathToDuplicate({ event, companyId: companyId ?? '' })
        : undefined,
    } as CurrentEvent;
  }, [event, i18n.language, defaultChannel?.slug, isWebView, companyId]);

  return { event: derivedState, ...rest };
};
